import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase';
import { collection, query, onSnapshot, orderBy } from 'firebase/firestore';
import { MemberModel, ManualVideo } from '../../../../models';
import { Play, Calendar, Search, BookOpen, Youtube, ExternalLink, ArrowLeft } from 'lucide-react';
import Header from '../studentFrame/header';

interface StudentMenualProps {
    member: MemberModel;
}

const StudentMenual: React.FC<StudentMenualProps> = ({ member }) => {
    const [videos, setVideos] = useState<ManualVideo[]>([]);
    const [selectedVideo, setSelectedVideo] = useState<ManualVideo | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const q = query(collection(db, 'manuals'), orderBy('createdAt', 'desc'));
        const unsubscribe = onSnapshot(q, (snap) => {
            const list: ManualVideo[] = [];
            snap.forEach(d => {
                const data = d.data() as ManualVideo;
                if (data.targetAudience.includes('student')) {
                    const matchesBranch = !data.branchId || data.branchId === 'all' || data.branchId === member.branchId;
                    if (matchesBranch) {
                        list.push({ id: d.id, ...data });
                    }
                }
            });
            setVideos(list);
            setIsLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const filteredVideos = videos.filter(v =>
        v.title.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (selectedVideo) {
        return (
            <div className="space-y-6 animate-in fade-in duration-300">
                {/* Header */}
                <div className="flex items-center gap-4 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                    <button
                        onClick={() => setSelectedVideo(null)}
                        className="w-10 h-10 rounded-xl bg-slate-50 text-slate-400 hover:bg-slate-100 transition-all flex items-center justify-center"
                    >
                        <ArrowLeft size={20} />
                    </button>
                    <div>
                        <div className="flex items-center gap-2 mb-1">
                            <div className="w-7 h-7 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center">
                                <Youtube size={14} />
                            </div>
                            <h2 className="text-xl font-black text-slate-800 tracking-tight">{selectedVideo.title}</h2>
                        </div>
                        <p className="text-xs font-medium text-slate-400 flex items-center gap-1.5">
                            <Calendar size={12} />
                            {new Date(selectedVideo.createdAt).toLocaleDateString()}
                        </p>
                    </div>
                </div>

                {/* Video Player */}
                <div className="bg-black rounded-3xl overflow-hidden shadow-2xl aspect-video mx-auto max-w-5xl">
                    <iframe
                        width="100%"
                        height="100%"
                        src={`https://www.youtube.com/embed/${selectedVideo.videoId}?autoplay=1`}
                        title={selectedVideo.title}
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="w-full h-full"
                    ></iframe>
                </div>

                {/* Details */}
                <div className="bg-white p-8 rounded-2xl border border-slate-100 shadow-sm">
                    <div className="flex items-center justify-between mb-6">
                        <span className="px-3 py-1 bg-indigo-50 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-widest">
                            영상 매뉴얼 가이드
                        </span>
                        <a
                            href={selectedVideo.youtubeUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-bold text-slate-400 hover:text-indigo-600 flex items-center gap-1.5 transition-colors"
                        >
                            <ExternalLink size={14} />
                            유튜브에서 보기
                        </a>
                    </div>
                    <div className="prose prose-slate max-w-none">
                        <p className="text-slate-600 font-medium leading-relaxed whitespace-pre-wrap">
                            {selectedVideo.description || '상세 설명이 없습니다.'}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500">
            {/* Header */}
            {/* Header */}
            <Header
                icon={BookOpen}
                title="매뉴얼 센터"
                subtitle="시스템 사용법과 학습 가이드 영상을 확인하세요."
            />

            {/* Search */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                    type="text"
                    placeholder="매뉴얼 제목으로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border border-slate-100 rounded-xl pl-11 pr-4 py-3 text-sm font-medium shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300"
                />
            </div>

            {/* Grid */}
            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20 gap-4">
                    <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-600 rounded-full animate-spin"></div>
                    <p className="text-slate-400 font-bold">매뉴얼을 불러오는 중입니다...</p>
                </div>
            ) : filteredVideos.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredVideos.map(video => (
                        <button
                            key={video.id}
                            onClick={() => setSelectedVideo(video)}
                            className="group bg-white rounded-2xl border border-slate-100 hover:border-indigo-200 transition-all overflow-hidden shadow-sm hover:shadow-xl text-left"
                        >
                            <div className="aspect-video relative overflow-hidden">
                                <img
                                    src={`https://img.youtube.com/vi/${video.videoId}/mqdefault.jpg`}
                                    alt={video.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                                />
                                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
                                    <div className="w-10 h-10 rounded-full bg-white/90 flex items-center justify-center text-indigo-600 opacity-0 group-hover:opacity-100 transform scale-50 group-hover:scale-100 transition-all duration-300 shadow-lg">
                                        <Play size={18} className="fill-indigo-600 ml-0.5" />
                                    </div>
                                </div>
                            </div>
                            <div className="p-4">
                                <h3 className="text-sm font-bold text-slate-800 line-clamp-1 mb-1 group-hover:text-indigo-600 transition-colors">
                                    {video.title}
                                </h3>
                                <p className="text-[11px] text-slate-400 line-clamp-2 min-h-[32px] mb-3 leading-relaxed">
                                    {video.description || '상세 설명이 없습니다.'}
                                </p>
                                <div className="flex items-center gap-1.5 text-[10px] font-bold text-slate-300 uppercase">
                                    <Calendar size={12} />
                                    {new Date(video.createdAt).toLocaleDateString()}
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            ) : (
                <div className="py-24 text-center bg-white rounded-[3rem] border border-slate-100 shadow-sm border-dashed">
                    <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 text-slate-200">
                        <Youtube size={40} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800">등록된 매뉴얼이 없습니다</h3>
                    <p className="text-sm font-bold text-slate-400 mt-2">새로운 소식이 있을 때까지 기다려 주세요!</p>
                </div>
            )}
        </div>
    );
};

export default StudentMenual;
